import React from 'react';
import { faHandHoldingUsd, IconDefinition, faPiggyBank, faFile, faCar } from '@fortawesome/free-solid-svg-icons';
import {
  faTachometerAlt,
  faFingerprint,
  faExclamation,
  faPaintRoller,
  faCreditCard,
  faPercentage,
  faUniversity,
  faPoundSign,
  faLightbulb,
  faMoneyBill,
  faShieldAlt,
  faStopwatch,
  faChartLine,
  faThumbsUp,
  faPhoneAlt,
  faEnvelope,
  faComments,
  faChartBar,
  faCompass,
  faMobile,
  faTrophy,
  faTools,
  faCheck,
  faUser,
  faLock,
  faBolt,
  faStar,
  faSun,
} from '@fortawesome/free-solid-svg-icons';

export const datoCMSIconsList: Record<string, IconDefinition> = {
  ['hand-holding-gbp']: faHandHoldingUsd,
  ['chart-upwards-trend']: faChartLine,
  ['tachometer-alt']: faTachometerAlt,
  ['percentage-sign']: faPercentage,
  ['spanner-screwdriver']: faTools,
  ['paint-roller']: faPaintRoller,
  ['credit-card']: faCreditCard,
  ['chat-bubbles']: faComments,
  ['light-bulb']: faLightbulb,
  ['line-chart']: faChartLine,
  ['piggy-bank']: faPiggyBank,
  ['pound-sign']: faPoundSign,
  ['cash-note']: faMoneyBill,
  exclamation: faExclamation,
  fingerprint: faFingerprint,
  ['lightning-bolt']: faBolt,
  ['bar-chart']: faChartBar,
  ['thumbs-up']: faThumbsUp,
  university: faUniversity,
  stopwatch: faStopwatch,
  telephone: faPhoneAlt,
  envelope: faEnvelope,
  shield: faShieldAlt,
  checkmark: faCheck,
  compass: faCompass,
  lightning: faBolt,
  mobile: faMobile,
  trophy: faTrophy,
  file: faFile,
  lock: faLock,
  star: faStar,
  user: faUser,
  car: faCar,
  sun: faSun,
};

export const isCounterIcon = (resolvedIcon: string) => /^counter_\d+$/.test(resolvedIcon);
// Custom SVG icons — rendered inside .b2b-icon so they inherit squircle bg + dark-section colour swap
export const customSvgIcons: Record<string, React.ReactElement> = {
  paypal: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" aria-hidden="true" height="32" fill="none" width="32">
      <path
        d="M18.3925 32.626H15.1063C14.5142 32.626 14.0518 32.1144 14.1114 31.5253L15.4252 18.5455L16.9381 13.1342C17.1695 12.3065 17.9024 11.7177 18.7614 11.6884C22.265 11.5685 27.843 11.56 30.1521 12.2343C33.4776 13.2053 31.5773 19.0315 30.1521 22.4301C29.2359 24.615 25.3952 25.065 22.5248 24.9896C21.4686 24.9618 20.5023 25.6677 20.3116 26.7069L19.376 31.8064C19.2889 32.2812 18.8751 32.626 18.3925 32.626Z"
        fill="currentColor"
      />
      <path
        d="M12.8925 28.626H9.60633C9.01422 28.626 8.55178 28.1144 8.61141 27.5253L9.92519 14.5455L11.4381 9.13421C11.6695 8.30651 12.4024 7.71773 13.2614 7.68835C16.765 7.56852 22.343 7.55999 24.6521 8.23426C27.9776 9.2053 26.0773 15.0315 24.6521 18.4301C23.7359 20.615 19.8952 21.065 17.0248 20.9896C15.9686 20.9618 15.0023 21.6677 14.8116 22.7069L13.876 27.8064C13.7889 28.2812 13.3751 28.626 12.8925 28.626Z"
        fill="currentColor"
      />
    </svg>
  ),
};
export const getCounterDigit = (resolvedIcon: string) => resolvedIcon.replace('counter_', '');
export const isCustomSvgIcon = (icon: string) => icon in customSvgIcons;

export const datoCMSMaterialIcons: Record<string, string> = {
  ['chart-upwards-trend']: 'trending_up',
  ['hand-holding-gbp']: 'payments',
  ['spanner-screwdriver']: 'build',
  ['paint-roller']: 'format_paint',
  ['pound-sign']: 'currency_pound',
  ['percentage-sign']: 'percent',
  ['credit-card']: 'credit_card',
  ['cash-note']: 'attach_money',
  university: 'account_balance',
  ['line-chart']: 'show_chart',
  exclamation: 'priority_high',
  ['tachometer-alt']: 'speed',
  ['light-bulb']: 'lightbulb',
  fingerprint: 'fingerprint',
  ['lightning-bolt']: 'bolt',
  ['bar-chart']: 'bar_chart',
  ['piggy-bank']: 'savings',
  ['thumbs-up']: 'thumb_up',
  ['chat-bubbles']: 'chat',
  schedule: 'watch_later',
  trophy: 'emoji_events',
  car: 'directions_car',
  mobile: 'smartphone',
  clock: 'watch_later',
  file: 'description',
  stopwatch: 'timer',
  checkmark: 'check',
  compass: 'explore',
  telephone: 'call',
  lightning: 'bolt',
  envelope: 'mail',
  shield: 'shield',
  sun: 'wb_sunny',
  user: 'person',
  tick: 'check',
  lock: 'lock',
  star: 'star',
  // Figma B2B icon set
  // Note: 'paypal' is intentionally absent — it uses a custom SVG via customSvgIcons, not a Material Symbol
  click: 'arrow_selector_tool',
  industry: 'account_balance',
  'arrow-up': 'arrow_upward',
  question: 'question_mark',
  arrowup: 'arrow_upward',
  pound: 'currency_pound',
  'sync-alt': 'sync_alt',
  three: 'counter_3',
  seven: 'counter_7',
  four: 'counter_4',
  five: 'counter_5',
  sync: 'sync_alt',
  one: 'counter_1',
  two: 'counter_2',
  six: 'counter_6',
  cross: 'close',
  home: 'home',
  plus: 'add',
};

// Icons that need FILL 1 to look correct (solid shape, not just bold strokes)
const filledIconKeys = new Set([
  'arrow_selector_tool',
  'emoji_events',
  'piggy-bank',
  'thumbs-up',
  'checkmark',
  'thumb_up',
  'savings',
  'trophy',
  'person',
  'check',
  'click',
  'star',
  'tick',
  'user',
]);
export const shouldFillIcon = (icon: string): boolean => {
  const resolved = datoCMSMaterialIcons[icon] ?? icon;
  return filledIconKeys.has(icon) || filledIconKeys.has(resolved);
};
